import React, { useEffect, useState } from "react";
import { Building, Download, ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import CommonDateRangePicker from "../../../Common/CommonDateRangePicker/CommonDateRangePicker";
import { combineAttendance } from "./attendanceUtils";
import { Link, useLocation } from "react-router-dom";

const HRMSDeptConsolidated = () => {
  const { state } = useLocation();
  const employees = state?.employees || [];
  const logs = state?.logs || [];
  const rosters = state?.rosters || {};
  const leaves = state?.leaves || [];
  const rangeFromParent = state?.range || { startDate: "", endDate: "" };

  const [range, setRange] = useState(rangeFromParent);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employees || employees.length === 0) return;
    if (!range.startDate || !range.endDate) {
      setAttendance([]);
      return;
    }

    setLoading(true);
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const allDates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      allDates.push(d.toISOString().split("T")[0]);
    }

    const all = [];
    allDates.forEach((date) => {
      const dayData = combineAttendance(date, employees, logs, rosters);
      if (dayData) all.push(...dayData);
    });

    setAttendance(all);
    setLoading(false);
  }, [range, employees, logs, rosters]);

  const filteredLeaves = leaves.filter((leave) => {
    if (!range.startDate || !range.endDate) return leaves;
    const from = new Date(leave.start_date);
    const to = new Date(leave.end_date);
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    return from <= end && to >= start;
  });

  const lateComers = attendance.filter((a) => a.status && a.status.startsWith("Late"));
  const earlyGoers = attendance.filter((a) => a.status && a.status.startsWith("Left"));

  const handleDownload = () => {
    if (attendance.length === 0 && filteredLeaves.length === 0) {
      Swal.fire("No Data", "No report data to download", "warning");
      return;
    }

    const wb = XLSX.utils.book_new();

    if (lateComers.length > 0) {
      const ws1 = XLSX.utils.json_to_sheet(lateComers);
      XLSX.utils.book_append_sheet(wb, ws1, "Late Comers");
    }
    if (earlyGoers.length > 0) {
      const ws2 = XLSX.utils.json_to_sheet(earlyGoers);
      XLSX.utils.book_append_sheet(wb, ws2, "Early Goers");
    }
    if (filteredLeaves.length > 0) {
      const ws3 = XLSX.utils.json_to_sheet(filteredLeaves);
      XLSX.utils.book_append_sheet(wb, ws3, "Leaves");
    }
    if (attendance.length > 0) {
      const ws4 = XLSX.utils.json_to_sheet(attendance);
      XLSX.utils.book_append_sheet(wb, ws4, "Full Attendance");
    }

    XLSX.writeFile(wb, `Consolidated_Report_${range.startDate}_to_${range.endDate}.xlsx`);
  };

  if (!state) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
        <div className="text-center py-5">
          <h5>Please open this report from the <strong>HRMS Reports</strong> page.</h5>
          <Link to="/hrms-reports" className="btn btn-primary mt-3">Go to Reports</Link>
        </div>
      </div>
    );
  }

  const renderTable = (title, data, type) => (
    <div className="mt-5">
      <h5 className="fw-semibold fs-4 text-primary mb-3">{title}</h5>
      <div className="table-responsive">
        <table className="table table-hover m-0">
          <thead>
            {type === "leave" ? (
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Department</th>
                <th>Leave Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Status</th>
              </tr>
            ) : (
              <tr>
                <th>Date</th>
                <th>Employee</th>
                <th>Department</th>
                <th>Shift</th>
                <th>Entry / Exit</th>
                <th>Worked Hours</th>
                <th>Status</th>
              </tr>
            )}
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, i) =>
                type === "leave" ? (
                  <tr key={i}>
                    <td>{item.employee_code}</td>
                    <td>{item.employee_name}</td>
                    <td>{item.department}</td>
                    <td>{item.leave_type}</td>
                    <td>{item.start_date}</td>
                    <td>{item.end_date}</td>
                    <td>{item.total_days}</td>
                    <td>{item.status}</td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td>{item.date}</td>
                    <td>{item.name}</td>
                    <td>{item.department}</td>
                    <td>{item.shiftStart} - {item.shiftEnd}</td>
                    <td>{item.entry} / {item.exit}</td>
                    <td>{item.workedHours}</td>
                    <td>{item.status}</td>
                  </tr>
                )
              )
            ) : (
              <tr>
                <td colSpan={type === "leave" ? 8 : 7} className="text-center py-3 text-muted">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="hrms-report-dept-cont">
            <div className="d-flex flex-column flex-lg-row row-gap-3 justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">
                <Building size={35} stroke="#302867" />
                <div className="d-flex flex-column">
                  <h4 className="fw-semibold m-0">Consolidated Report</h4>
                  <span className="text-muted">Consolidated audit report for each department.</span>
                </div>
              </div>
              <Link to="/hrms-reports" className="d-flex align-items-center gap-2 btn hrms-reports-back-btn">
                <ArrowLeft size={20} />
                Back to Reports
              </Link>
            </div>

            <div className="row mt-3 row-gap-2">
              <div className="col-lg-10 col-sm-12 col-12">
                <CommonDateRangePicker label="Date Range" value={range} onChange={setRange} />
              </div>
              <div className="col-lg-2 col-sm-12 col-12 d-flex align-items-end">
                <button className="btn hrms-report-btn w-100" onClick={handleDownload}>
                  <Download size={18} stroke="#fff" />
                  <span className="text-light">Download Report</span>
                </button>
              </div>
            </div>

            {renderTable("Late Comers", lateComers)}
            {renderTable("Early Goers", earlyGoers)}
            {renderTable("Leaves", filteredLeaves, "leave")}
            {renderTable("All Attendance", attendance)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRMSDeptConsolidated;
